import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { CAD } from "../../lib/format";

const COLORS = ["#0F4C3A", "#E07A5F", "#E9B949", "#3A7CA5", "#84A98C", "#B5651D"];

const tip = (value) => CAD(value);

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-white p-3 text-xs shadow-lg">
      {label && <div className="mb-1 font-semibold text-foreground">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold text-foreground">{CAD(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ChartBlock({ chart }) {
  if (!chart) return null;
  const { type, data, xKey = "x", series = [] } = chart;

  return (
    <div data-testid={`chart-${type}`} className="w-full min-w-0">
      {chart.title && <h4 className="mb-3 font-heading text-sm font-bold text-foreground">{chart.title}</h4>}
      <div className="h-64 w-full min-w-0">
        <ResponsiveContainer width="99%" height="100%" debounce={1}>
          {type === "pie" ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={tip} />
              <Legend />
            </PieChart>
          ) : type === "line" ? (
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF1EF" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
              <Tooltip content={<ChartTooltip />} />
              {series.length > 1 && <Legend />}
              {series.map((s, i) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={false} />
              ))}
            </LineChart>
          ) : type === "stackedArea" ? (
            <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF1EF" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              {series.map((s, i) => (
                <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stackId="1"
                  stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.75} />
              ))}
            </AreaChart>
          ) : (
            <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF1EF" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} width={44} />
              <Tooltip content={<ChartTooltip />} />
              {series.length > 1 && <Legend />}
              {series.map((s, i) => (
                <Bar key={s.key} dataKey={s.key} name={s.name} fill={COLORS[i % COLORS.length]} radius={[6, 6, 0, 0]} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
